interface Section {
  title: string;
  body: string;
}

interface Subsection {
  title: string;
  body: string;
}

// Strip the minimum leading indent across non-blank lines. Some exporters emit
// the whole document with a uniform leading indent (seen with models that
// quote the output as if it were a nested block); without this, every
// heading-level regex below would silently miss those headings. We strip the
// *common* prefix only, not all whitespace, so intentional internal indent
// inside code blocks and nested lists is preserved.
function stripCommonIndent(body: string): string {
  const lines = body.split("\n");
  let min = Number.POSITIVE_INFINITY;
  for (const line of lines) {
    if (line.trim().length === 0) continue;
    const m = line.match(/^[ \t]*/);
    const indent = m ? m[0].length : 0;
    if (indent < min) min = indent;
    if (min === 0) break;
  }
  if (!Number.isFinite(min) || min === 0) return body;
  return lines.map((line) => (line.trim().length === 0 ? line : line.slice(min))).join("\n");
}

export function splitH2Sections(body: string): Section[] {
  const lines = stripCommonIndent(body).split("\n");
  const sections: Section[] = [];
  let currentTitle: string | null = null;
  let buffer: string[] = [];

  const flush = () => {
    if (currentTitle !== null) {
      sections.push({ title: currentTitle, body: buffer.join("\n") });
    }
  };

  for (const line of lines) {
    const match = line.match(/^##\s+(.+?)\s*$/);
    if (match && !line.startsWith("###")) {
      flush();
      currentTitle = (match[1] ?? "").trim();
      buffer = [];
    } else {
      buffer.push(line);
    }
  }
  flush();
  return sections;
}

export function splitH3Entries(sectionBody: string): Subsection[] {
  const lines = stripCommonIndent(sectionBody).split("\n");
  const entries: Subsection[] = [];
  let currentTitle: string | null = null;
  let buffer: string[] = [];

  const flush = () => {
    if (currentTitle !== null) {
      entries.push({ title: currentTitle, body: buffer.join("\n") });
    }
  };

  for (const line of lines) {
    const match = line.match(/^###\s+(.+?)\s*$/);
    if (match && !line.startsWith("####")) {
      flush();
      currentTitle = (match[1] ?? "").trim();
      buffer = [];
    } else {
      buffer.push(line);
    }
  }
  flush();
  return entries;
}

function splitH4Subsections(body: string): Subsection[] {
  const lines = stripCommonIndent(body).split("\n");
  const entries: Subsection[] = [];
  let currentTitle: string | null = null;
  let buffer: string[] = [];

  const flush = () => {
    if (currentTitle !== null) {
      entries.push({ title: currentTitle, body: buffer.join("\n") });
    }
  };

  for (const line of lines) {
    const match = line.match(/^####\s+(.+?)\s*$/);
    if (match) {
      flush();
      currentTitle = (match[1] ?? "").trim();
      buffer = [];
    } else if (currentTitle !== null) {
      buffer.push(line);
    }
  }
  flush();
  return entries;
}

export function findH4Body(body: string, name: string): string | null {
  const subs = splitH4Subsections(body);
  const target = name.toLowerCase();
  for (const sub of subs) {
    if (sub.title.toLowerCase() === target) return sub.body;
  }
  return null;
}

export function stripH4Subsections(body: string): string {
  const lines = body.split("\n");
  const out: string[] = [];
  let inH4 = false;
  for (const line of lines) {
    if (/^####\s+/.test(line)) {
      inH4 = true;
      continue;
    }
    if (!inH4) out.push(line);
  }
  return out.join("\n");
}

export function extractBlockQuote(body: string): string | undefined {
  const lines = body.split("\n");
  const out: string[] = [];
  for (const line of lines) {
    const m = line.match(/^>\s?(.*)$/);
    if (m) out.push(m[1] ?? "");
    else if (out.length > 0 && line.trim().length === 0) out.push("");
    else if (out.length > 0) break;
  }
  while (out.length > 0 && out[out.length - 1]?.trim() === "") out.pop();
  return out.length > 0 ? out.join("\n") : undefined;
}

export function stripBlockQuotes(body: string): string {
  return body
    .split("\n")
    .filter((line) => !/^>\s?/.test(line))
    .join("\n");
}
